<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

type PrimitiveValue = string | number
type ItemObject = Record<string, unknown>
type Item = ItemObject | PrimitiveValue

const props = withDefaults(
  defineProps<{
    modelValue: PrimitiveValue | null
    items: Item[]
    label?: string
    itemTitle?: string
    itemValue?: string
    placeholder?: string
    noDataText?: string
  }>(),
  {
    label: undefined,
    itemTitle: 'title',
    itemValue: 'value',
    placeholder: 'Selecionar campo',
    noDataText: 'Nenhum resultado encontrado',
  }
)

const emit = defineEmits<{
  (e: 'update:modelValue', value: PrimitiveValue | null): void
}>()

const search = ref('')

function isPrimitiveValue(value: unknown): value is PrimitiveValue {
  return typeof value === 'string' || typeof value === 'number'
}

const getTitle = (item: Item): string => {
  if (isPrimitiveValue(item)) return String(item)

  const value = item[props.itemTitle]
  return isPrimitiveValue(value) ? String(value) : ''
}

const internalValue = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value),
})

const filteredItems = computed(() => {
  const term = search.value.trim().toLowerCase()

  if (!term) return props.items

  return props.items.filter((item) =>
    getTitle(item).toLowerCase().includes(term)
  )
})

function handleMenuUpdate(open: boolean) {
  if (!open) search.value = ''
}

function clearSelection() {
  emit('update:modelValue', null)
}
</script>

<template>
  <v-select
    v-model="internalValue"
    :items="filteredItems"
    :item-title="itemTitle"
    :item-value="itemValue"
    :placeholder="placeholder"
    variant="outlined"
    density="compact"
    hide-details
    single-line
    menu-icon="mdi-menu-down"
    class="searchable-select"
    :menu-props="{ maxHeight: 520 }"
    @update:menu="handleMenuUpdate"
  >
    <template #prepend-item>
      <div class="searchable-select__search-wrap">
        <v-text-field
          v-model="search"
          placeholder="Buscar"
          variant="plain"
          density="compact"
          hide-details
          single-line
          autofocus
          append-inner-icon="mdi-magnify"
          class="searchable-select__search-input"
          @keydown.stop
        />
      </div>

      <div class="searchable-select__divider" />
    </template>

    <template #item="{ props: itemProps, item }">
      <v-list-item
        v-bind="itemProps"
        :title="getTitle(item.raw)"
        class="searchable-select__item"
      />
    </template>

    <template #selection="{ item }">
      <span class="searchable-select__selection">
        {{ getTitle(item.raw) }}
      </span>
    </template>

    <template #append-item>
      <div class="searchable-select__divider" />

      <button
        type="button"
        class="searchable-select__clear"
        @click.stop="clearSelection"
      >
        {{ t('contacts.import.mapping.ignoreField') }}
      </button>
    </template>
  </v-select>
</template>

<style scoped>
.searchable-select {
  width: 100%;
  align-self: stretch;
}

/* =========================
   CAMPO FECHADO
========================= */

:deep(.searchable-select .v-input__control) {
  width: 100%;
}

:deep(.searchable-select .v-field) {
  min-height: 32px !important;
  height: 32px !important;
  max-height: 32px !important;
  border-radius: 4px !important;
  background: #fff;
}

:deep(.searchable-select .v-field__field) {
  height: 32px;
  max-height: 32px !important;
}

:deep(.searchable-select .v-field__outline) {
  --v-field-border-opacity: 1;
}

:deep(.searchable-select .v-field__outline__start),
:deep(.searchable-select .v-field__outline__notch),
:deep(.searchable-select .v-field__outline__end) {
  border-color: rgba(0, 0, 0, 0.38) !important;
}

:deep(.searchable-select .v-field__input) {
  min-height: 32px !important;
  height: 32px !important;
  max-height: 32px !important;
  padding: 0 16px !important;
  display: flex;
  align-items: center;
  color: rgba(0, 0, 0, 0.87);
  font-family: 'Open Sans';
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
}

:deep(.searchable-select .v-select__selection) {
  margin: 0;
  display: flex;
  align-items: center;
  height: 20px;
}

.searchable-select__selection {
  overflow: hidden;
  display: block;
  width: 100%;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: rgba(0, 0, 0, 0.6);
  font-family: 'Open Sans';
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 18px;
}

:deep(.searchable-select .v-field__append-inner) {
  height: 32px;
  max-height: 32px !important;
  padding-right: 12px;
  display: flex;
  align-items: center;
}

:deep(.searchable-select .v-field__append-inner .v-icon) {
  font-size: 18px;
  color: rgba(0, 0, 0, 0.54);
}

/* remove espaçamentos extras do vuetify */
:deep(.searchable-select .v-field__prepend-inner),
:deep(.searchable-select .v-field__clearable) {
  padding-top: 0 !important;
}

/* =========================
   DROPDOWN
========================= */

:deep(.v-overlay__content .v-list) {
  padding: 0;
  background: #fff;
}

/* =========================
   BUSCA
========================= */

.searchable-select__search-wrap {
  padding: 0 32px;
  background: #fff;
}

:deep(.searchable-select__search-input .v-field) {
  min-height: 56px !important;
  height: 56px !important;
  background: transparent !important;
  border: 0 !important;
  border-radius: 0 !important;
  box-shadow: none !important;
}

:deep(.searchable-select__search-input .v-field__outline) {
  display: none !important;
}

:deep(.searchable-select__search-input .v-field__input) {
  min-height: 56px !important;
  height: 56px !important;
  padding: 0 !important;
  color: rgba(0, 0, 0, 0.6) !important;
  font-family: 'Open Sans';
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
}

:deep(.searchable-select__search-input input::placeholder) {
  color: rgba(0, 0, 0, 0.6);
  opacity: 1;
}

:deep(.searchable-select__search-input .v-field__append-inner) {
  height: 56px;
  padding: 0 !important;
  display: flex;
  align-items: center;
  align-self: stretch;
}

:deep(.searchable-select__search-input .v-icon) {
  font-size: 24px;
  color: rgba(0, 0, 0, 0.54);
  display: flex;
  align-items: center;
  justify-content: center;
}

/* =========================
   DIVIDER
========================= */

.searchable-select__divider {
  width: 100%;
  height: 1px;
  margin: 0;
  background: rgba(0, 0, 0, 0.12);
}

/* =========================
   ITENS
========================= */

:deep(.searchable-select__item) {
  height: 48px;
  padding: 0 16px;
  display: flex;
  align-items: center;
}

:deep(.searchable-select__item .v-list-item-title) {
  color: rgba(0, 0, 0, 0.87);
  font-family: 'Open Sans';
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 19px;
}

:deep(.searchable-select__item:hover) {
  background: rgba(0, 0, 0, 0.04);
}

/* =========================
   BOTÃO LIMPAR
========================= */

.searchable-select__clear {
  width: 100%;
  height: 48px;
  padding: 0 16px;
  border: 0;
  background: #fff;
  text-align: left;
  cursor: pointer;
  color: #ff5252;
  font-family: 'Open Sans';
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 19px;
}

.searchable-select__clear:hover {
  background: rgba(255, 82, 82, 0.04);
}
</style>
